'use client'

import { Check } from 'lucide-react'

import { BlockMini, GROUP_TONE } from '@/components/dashboard/BlockMini'
import { PERSONA_SLUGS, type PersonaSlug } from '@/lib/personas'
import { useT } from '../i18n/LocaleProvider'
import { PERSONA_SIGNATURE } from './persona-start'

/**
 * Sign-up's first question: who the page is for.
 *
 * Asked before anything is typed because everything after it changes with
 * the answer: the words, the blocks the preview shows, whether a phone is
 * needed, the first block suggested. A seventeen-year-old reading "bookings,
 * products, payments" should not have to wonder whether this is for them.
 *
 * Rows rather than a grid of cards, each carrying the block its persona leads
 * with, drawn as the editor draws it, so the choice is made by recognising a
 * page rather than by reading five descriptions.
 */
export function PersonaPicker({ value, onChange }: {
  value: PersonaSlug | null
  onChange: (_persona: PersonaSlug) => void
}) {
  const t = useT()

  return (
    <div role="radiogroup" aria-label={t('auth.persona.heading')} className="flex flex-col gap-2">
      { PERSONA_SLUGS.map(slug => {
        const tile = PERSONA_SIGNATURE[slug]
        const tone = GROUP_TONE[tile.group]
        const selected = value === slug
        return (
          <button
            key={slug}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(slug)}
            className={`group flex cursor-pointer items-center gap-3.5 rounded-2xl border p-1.5 pr-4 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
              selected ? 'border-foreground/70 bg-card' : 'border-border hover:border-input'
            }`}
          >
            <span
              className={`flex h-14 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl transition-[filter] ${selected ? '' : 'saturate-[.35] group-hover:saturate-100'}`}
              style={{ background: `radial-gradient(120% 100% at 0% 0%, rgb(255 255 255 / 0.28), transparent 55%), linear-gradient(135deg, ${tone.from}, ${tone.to})` }}
              aria-hidden="true"
            >
              {/* Scaled down: the catalog draws these for a taller stage. */}
              <span className="scale-[.72]"><BlockMini type={tile.type} group={tile.group} /></span>
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="font-gilroy text-[15px] font-bold leading-tight text-foreground">{t(`auth.persona.${slug}.name`)}</span>
              <span className="text-[13px] font-medium leading-snug text-muted-foreground">{t(`auth.persona.${slug}.line`)}</span>
            </span>
            <span
              className={`ml-auto flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                selected ? 'border-transparent bg-foreground text-background' : 'border-input'
              }`}
              aria-hidden="true"
            >
              { selected && <Check className="size-3.5" strokeWidth={3} /> }
            </span>
          </button>
        )
      }) }
    </div>
  )
}
